import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Radio, Mic, Play, Trash2, Edit2, Plus, X, Save, 
  ChevronLeft, Clock, Calendar, Link as LinkIcon, 
  Image, Video, Power, PowerOff, MessageCircle
} from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLive = () => {
  const [liveSettings, setLiveSettings] = useState({
    is_live: false,
    stream_url: '',
    stream_title: '',
    stream_description: '',
    next_stream_date: '',
    next_stream_title: ''
  });
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPodcastModal, setShowPodcastModal] = useState(false);
  const [editingPodcast, setEditingPodcast] = useState(null);
  const [chatCount, setChatCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const fromParam = new URLSearchParams(location.search).get('from');
  const backTo = fromParam ? decodeURIComponent(fromParam) : '/admin/dashboard';

  const [podcastForm, setPodcastForm] = useState({
    title: '',
    description: '',
    audio_url: '',
    video_url: '',
    thumbnail: '',
    duration: '',
    is_published: true
  });

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      // Fetch live settings
      const liveRes = await fetch(`${BACKEND_URL}/api/live/status`);
      const liveData = await liveRes.json();
      setLiveSettings(liveData);

      // Fetch podcasts
      const podRes = await adminFetch('/podcasts?published_only=false');
      const podData = await podRes.json();
      setPodcasts(podData);

      // Fetch chat count
      const chatRes = await fetch(`${BACKEND_URL}/api/live/chat`);
      const chatData = await chatRes.json();
      setChatCount(chatData.length);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLiveSettings = async () => {
    try {
      await adminFetch('/admin/live/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(liveSettings)
      });
      toast.success('Live settings saved!');
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const handleToggleLive = async () => {
    try {
      const res = await adminFetch('/admin/live/toggle', { method: 'POST' });
      const data = await res.json();
      setLiveSettings(prev => ({ ...prev, is_live: data.is_live }));
      toast.success(data.is_live ? 'You are now LIVE!' : 'Stream ended');
    } catch (err) {
      toast.error('Failed to toggle live status');
    }
  };

  const handleClearChat = async () => {
    if (!confirm('Are you sure you want to clear all chat messages?')) return;
    try {
      await adminFetch('/admin/live/chat', { method: 'DELETE' });
      setChatCount(0);
      toast.success('Chat cleared');
    } catch (err) {
      toast.error('Failed to clear chat');
    }
  };

  const handleSavePodcast = async (e) => {
    e.preventDefault();
    try {
      if (editingPodcast) {
        await adminFetch(`/admin/podcasts/${editingPodcast.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(podcastForm)
        });
        toast.success('Episode updated!');
      } else {
        await adminFetch('/admin/podcasts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(podcastForm)
        });
        toast.success('Episode created!');
      }
      setShowPodcastModal(false);
      setEditingPodcast(null);
      resetPodcastForm();
      fetchData();
    } catch (err) {
      toast.error('Failed to save episode');
    }
  };

  const handleDeletePodcast = async (id) => {
    if (!confirm('Delete this podcast episode?')) return;
    try {
      await adminFetch(`/admin/podcasts/${id}`, { method: 'DELETE' });
      toast.success('Episode deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete episode');
    }
  };

  const openEditPodcast = (podcast) => {
    setEditingPodcast(podcast);
    setPodcastForm({
      title: podcast.title,
      description: podcast.description,
      audio_url: podcast.audio_url || '',
      video_url: podcast.video_url || '',
      thumbnail: podcast.thumbnail || '',
      duration: podcast.duration || '',
      is_published: podcast.is_published
    });
    setShowPodcastModal(true);
  };

  const resetPodcastForm = () => {
    setPodcastForm({
      title: '',
      description: '',
      audio_url: '',
      video_url: '',
      thumbnail: '',
      duration: '',
      is_published: true
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(backTo)}
            data-testid="admin-live-back"
            className="p-2 hover:bg-white/5 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <div>
            <h1 className="text-3xl font-bold font-display text-foreground flex items-center gap-3">
              <Radio className="w-8 h-8 text-primary" />
              Live & Podcast Management
            </h1>
            <p className="text-muted-foreground mt-1">Manage live streams, podcasts, and chat</p>
          </div>
        </div>

        {/* Live Stream Settings */}
        <div className="bg-card border border-border/50 rounded-2xl p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Video className="w-5 h-5 text-red-500" />
              Live Stream Settings
            </h2>
            <button
              onClick={handleToggleLive}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all ${
                liveSettings.is_live 
                  ? 'bg-red-500 hover:bg-red-600 text-white' 
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
              data-testid="toggle-live-btn"
            >
              {liveSettings.is_live ? <PowerOff className="w-5 h-5" /> : <Power className="w-5 h-5" />}
              {liveSettings.is_live ? 'End Stream' : 'Go Live'}
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-foreground mb-2">
                Stream URL (YouTube/Vimeo)
              </label>
              <div className="relative">
                <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="url"
                  value={liveSettings.stream_url || ''}
                  onChange={(e) => setLiveSettings(prev => ({ ...prev, stream_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full pl-12 pr-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-foreground mb-2">Stream Title</label>
              <input
                type="text"
                value={liveSettings.stream_title || ''}
                onChange={(e) => setLiveSettings(prev => ({ ...prev, stream_title: e.target.value }))}
                placeholder="Casa Samba Live Performance"
                className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-foreground mb-2">Stream Description</label>
              <textarea
                value={liveSettings.stream_description || ''}
                onChange={(e) => setLiveSettings(prev => ({ ...prev, stream_description: e.target.value }))}
                placeholder="Join us for an amazing live performance..."
                rows={2}
                className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-foreground mb-2">
                Next Stream Date (for countdown)
              </label>
              <input
                type="datetime-local"
                value={liveSettings.next_stream_date || ''}
                onChange={(e) => setLiveSettings(prev => ({ ...prev, next_stream_date: e.target.value }))}
                className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-foreground mb-2">Next Stream Title</label>
              <input
                type="text"
                value={liveSettings.next_stream_title || ''}
                onChange={(e) => setLiveSettings(prev => ({ ...prev, next_stream_title: e.target.value }))}
                placeholder="Upcoming: Samba Night Live"
                className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 pt-6 border-t border-border/30">
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                {chatCount} chat messages
              </span>
              <button
                onClick={handleClearChat}
                className="text-sm text-red-400 hover:text-red-300 transition-colors"
              >
                Clear Chat
              </button>
            </div>
            <button
              onClick={handleSaveLiveSettings}
              className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-colors"
            >
              <Save className="w-5 h-5" />
              Save Settings
            </button>
          </div>
        </div>

        {/* Podcast Episodes */}
        <div className="bg-card border border-border/50 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Mic className="w-5 h-5 text-primary" />
              Podcast Episodes ({podcasts.length})
            </h2>
            <button
              onClick={() => { resetPodcastForm(); setEditingPodcast(null); setShowPodcastModal(true); }}
              className="flex items-center gap-2 px-5 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all"
              data-testid="add-podcast-btn"
            >
              <Plus className="w-5 h-5" />
              Add Episode
            </button>
          </div>

          {podcasts.length > 0 ? (
            <div className="space-y-4">
              {podcasts.map((podcast) => (
                <motion.div
                  key={podcast.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-4 p-4 bg-background rounded-xl border border-border/30"
                >
                  <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                    {podcast.thumbnail ? (
                      <img loading="lazy" decoding="async" src={podcast.thumbnail} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-primary/20 flex items-center justify-center">
                        <Mic className="w-8 h-8 text-primary" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-foreground truncate">{podcast.title}</h3>
                      {!podcast.is_published && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Draft</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{podcast.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      {podcast.duration && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {podcast.duration}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(podcast.published_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditPodcast(podcast)}
                      className="p-2 hover:bg-white/10 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePodcast(podcast.id)}
                      className="p-2 hover:bg-white/10 rounded-lg text-red-400 hover:text-red-300 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Mic className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No podcast episodes yet</p>
              <p className="text-sm">Click "Add Episode" to create your first podcast</p>
            </div>
          )}
        </div>
      </div>

      {/* Podcast Modal */}
      {showPodcastModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/50 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-foreground">
                {editingPodcast ? 'Edit Episode' : 'Add New Episode'}
              </h2>
              <button
                onClick={() => { setShowPodcastModal(false); setEditingPodcast(null); }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSavePodcast} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Title *</label>
                <input
                  type="text"
                  value={podcastForm.title}
                  onChange={(e) => setPodcastForm(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="podcast-title"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Description *</label>
                <textarea
                  value={podcastForm.description}
                  onChange={(e) => setPodcastForm(prev => ({ ...prev, description: e.target.value }))}
                  required
                  rows={3}
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  data-testid="podcast-description"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-foreground mb-2">
                  Video URL (YouTube/Vimeo)
                </label>
                <input
                  type="url"
                  value={podcastForm.video_url}
                  onChange={(e) => setPodcastForm(prev => ({ ...prev, video_url: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=..."
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Audio URL</label>
                <input
                  type="url"
                  value={podcastForm.audio_url}
                  onChange={(e) => setPodcastForm(prev => ({ ...prev, audio_url: e.target.value }))}
                  placeholder="https://example.com/audio.mp3"
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Thumbnail URL</label>
                <input
                  type="url"
                  value={podcastForm.thumbnail}
                  onChange={(e) => setPodcastForm(prev => ({ ...prev, thumbnail: e.target.value }))}
                  placeholder="https://example.com/thumbnail.jpg"
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Duration</label>
                <input
                  type="text"
                  value={podcastForm.duration}
                  onChange={(e) => setPodcastForm(prev => ({ ...prev, duration: e.target.value }))}
                  placeholder="45:30"
                  className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={podcastForm.is_published}
                  onChange={(e) => setPodcastForm(prev => ({ ...prev, is_published: e.target.checked }))}
                  className="w-5 h-5 rounded border-border bg-background text-primary focus:ring-primary"
                />
                <label htmlFor="is_published" className="text-foreground">Publish immediately</label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowPodcastModal(false); setEditingPodcast(null); }}
                  className="flex-1 px-4 py-3 border border-border rounded-xl text-foreground hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-bold transition-colors"
                  data-testid="save-podcast-btn"
                >
                  <Save className="w-4 h-4" />
                  {editingPodcast ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminLive;
